import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageHeader, StatusPill } from "@/components/admin/PageHeader";
import { adminPacksQuery } from "@/lib/packs/admin-queries";
import { deletePacksFn, duplicatePackFn, setPackStatusFn } from "@/lib/packs/packs.admin.functions";
import { PACK_STATUSES } from "@/lib/packs/admin-types";
import { salesChannelLabel } from "@/lib/catalog/sales-channels";
import type { PackStatus } from "@/lib/packs/types";

export const Route = createFileRoute("/admin/packs/")({ component: PacksAdminPage });

const TONE: Record<PackStatus, "success" | "warn" | "info" | "neutral"> = {
  published: "success",
  scheduled: "info",
  draft: "warn",
  archived: "neutral",
};

function PacksAdminPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data = [], isLoading, error } = useQuery(adminPacksQuery());
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"all" | PackStatus>("all");

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return data.filter((p) => {
      if (status !== "all" && p.status !== status) return false;
      if (!term) return true;
      return `${p.name} ${p.slug} ${p.salesChannel}`.toLowerCase().includes(term);
    });
  }, [data, q, status]);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["admin", "packs"] });

  const statusMut = useMutation({
    mutationFn: (v: { ids: string[]; status: PackStatus }) => setPackStatusFn({ data: v }),
    onSuccess: (_r, v) => { toast.success(`Pack marked ${v.status}`); invalidate(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const deleteMut = useMutation({
    mutationFn: (ids: string[]) => deletePacksFn({ data: { ids } }),
    onSuccess: () => { toast.success("Pack deleted"); invalidate(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const duplicateMut = useMutation({
    mutationFn: (id: string) => duplicatePackFn({ data: { id } }),
    onSuccess: () => { toast.success("Pack duplicated as draft"); invalidate(); },
    onError: (e: Error) => toast.error(e.message),
  });

  const busy = statusMut.isPending || deleteMut.isPending || duplicateMut.isPending;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Pack Builder"
        subtitle={`${data.length} packs · ${data.filter((p) => p.status === "published").length} published · not yet visible to customers`}
        action={
          <button
            type="button"
            className="border border-black bg-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white hover:bg-black/85"
            onClick={() => void navigate({ to: "/admin/packs/$id", params: { id: "new" } })}
          >
            New pack
          </button>
        }
      />

      <div className="flex flex-wrap items-center gap-3 rounded-sm border border-black/10 bg-white p-3">
        <input
          className="w-64 border border-black/15 px-3 py-2 text-sm"
          placeholder="Search packs…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select className="border border-black/15 px-3 py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value as never)}>
          <option value="all">All statuses</option>
          {PACK_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="overflow-x-auto rounded-sm border border-black/10 bg-white">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="border-b border-black/10 text-[10px] uppercase tracking-[0.18em] text-black/50">
            <tr>
              <th className="px-4 py-3">Pack</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Channel</th>
              <th className="px-4 py-3">Bottle size</th>
              <th className="px-4 py-3">Variants</th>
              <th className="px-4 py-3">Eligible products</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td className="px-4 py-6 text-black/60" colSpan={7}>Loading packs…</td></tr>}
            {error && <tr><td className="px-4 py-6 text-red-700" colSpan={7}>{(error as Error).message}</td></tr>}
            {!isLoading && !error && rows.length === 0 && (
              <tr><td className="px-4 py-6 text-black/60" colSpan={7}>No packs yet. Create your first pack to get started.</td></tr>
            )}
            {rows.map((p) => (
              <tr key={p.id} className="border-b border-black/5 last:border-0">
                <td className="px-4 py-3">
                  <div className="font-semibold text-black">{p.name}</div>
                  <div className="text-xs text-black/50">/{p.slug}</div>
                </td>
                <td className="px-4 py-3"><StatusPill tone={TONE[p.status]}>{p.status}</StatusPill></td>
                <td className="px-4 py-3">{salesChannelLabel(p.salesChannel)}</td>
                <td className="px-4 py-3">{p.bottleSizeMl} ML</td>
                <td className="px-4 py-3">{p.variants.map((v) => v.bottleCount).join(" / ") || "—"}</td>
                <td className="px-4 py-3">{p.eligibleProductCount}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap justify-end gap-2 text-[11px] font-semibold uppercase tracking-[0.14em]">
                    <button type="button" className="border border-black/20 px-2 py-1 hover:bg-black/5" onClick={() => void navigate({ to: "/admin/packs/$id", params: { id: p.id } })}>Edit</button>
                    <button type="button" disabled={busy} className="border border-black/20 px-2 py-1 hover:bg-black/5 disabled:opacity-50" onClick={() => duplicateMut.mutate(p.id)}>Duplicate</button>
                    {p.status === "published" ? (
                      <button type="button" disabled={busy} className="border border-black/20 px-2 py-1 hover:bg-black/5 disabled:opacity-50" onClick={() => statusMut.mutate({ ids: [p.id], status: "draft" })}>Unpublish</button>
                    ) : (
                      <button type="button" disabled={busy} className="border border-black/20 px-2 py-1 hover:bg-black/5 disabled:opacity-50" onClick={() => statusMut.mutate({ ids: [p.id], status: "published" })}>Publish</button>
                    )}
                    <button type="button" disabled={busy} className="border border-black/20 px-2 py-1 hover:bg-black/5 disabled:opacity-50" onClick={() => statusMut.mutate({ ids: [p.id], status: "archived" })}>Archive</button>
                    <button
                      type="button"
                      disabled={busy}
                      className="border border-red-300 px-2 py-1 text-red-700 hover:bg-red-50 disabled:opacity-50"
                      onClick={() => { if (window.confirm(`Delete “${p.name}”? This cannot be undone.`)) deleteMut.mutate([p.id]); }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}